import { Injectable, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { APIService } from "@core/services/api.service";
import { HttpClient } from "@angular/common/http";
import {
  Voting,
  VotingCookieData,
  VotingResultDisplayConfig,
  VotingSession,
  VotingSessionResponse,
} from "@modules/voting/models/voting";
import { SectionItem } from "@modules/voting/models/section-item";
import {
  LocalVotingStatus,
  VotingDisplayMode,
  VotingType,
} from "@modules/voting/models/enums";
import { CaptchaService } from "@core/services/captcha/captcha.service";
import { Subject, Subscription } from "rxjs";
import { CookieService } from "@utils/services/cookie/cookie.service";
import { environment } from "environments/environment";
import { EnvName } from "@utils/models/environment/env-name";

@Injectable()
export class VotingControlService implements OnDestroy {
  public localVotingStatus: LocalVotingStatus = LocalVotingStatus.OPEN_ELIGIBLE;
  private _displayMode: VotingDisplayMode = VotingDisplayMode.SUBMISSION;
  public votingId: string;

  public onRevoteAllowanceElapsed = new Subject();
  public onDisplayModeChange = new Subject();

  public resultDisplayConfig: VotingResultDisplayConfig = {
    showOnlyTopResults: false,
    nrOfVisibleItems: -1,
    showVotedItems: true,
  };

  voting: Voting;
  session: VotingSession;
  captchaResponse: string;
  captchaResolutionSubscription: Subscription;
  sessionTimeout: NodeJS.Timeout;

  static SORT_ITEMS_BY_RATING = (a: SectionItem, b: SectionItem) =>
    b.rating - a.rating;
  static SORT_ITEMS_BY_VOTES = (a: SectionItem, b: SectionItem) =>
    b.vote_count.total - a.vote_count.total;

  ngOnDestroy(): void {
    this.captchaResolutionSubscription?.unsubscribe();
    clearTimeout(this.sessionTimeout);
  }

  public set displayMode(mode: VotingDisplayMode) {
    this._displayMode = mode;
    this.onDisplayModeChange.next(mode);
  }

  public get displayMode() {
    return this._displayMode;
  }

  /**
   * Generate a token that will be processed on the server to legitimate valid votes
   * 1) take last x digits of current UTC timestamp
   * 2) reverse ts
   * 3) XOR sliced & reversed TS with voting id
   * 4) convert XOR result to hex string
   */
  generateVotingToken(votingId) {
    const sliceIndex = 7;
    const estimatedServerTs =
      this.session.serverTs + (Date.now() - this.session.localResponseTime);
    // take last x digits of timestamp, and reverse them
    const slicedTs = parseInt(
      Array.from(estimatedServerTs.toString().slice(sliceIndex))
        .reverse()
        .join(""),
      10
    );

    // tslint:disable-next-line:no-bitwise
    const xor = slicedTs ^ parseInt(votingId, 16);

    return xor.toString(16);
  }

  constructor(
    private activeRoute: ActivatedRoute,
    private apiService: APIService,
    private http: HttpClient,
    public captchaService: CaptchaService
  ) {
    this.captchaResolutionSubscription =
      this.captchaService.onCaptchaResolutionChange.subscribe((response) => {
        this.captchaResponse = response;
      });
  }

  public async fetchVoting() {
    const response = await this.http
      .get<any>(this.getVotingRoute(), this.apiService.getJsonOptions())
      .toPromise();
    this.voting = new Voting(response.data);
    this.sortItems(VotingControlService.SORT_ITEMS_BY_RATING);

    return this.voting;
  }

  public async openVotingSession() {
    if (this.session) {
      return;
    }

    const response: VotingSessionResponse = await this.http
      .post<VotingSessionResponse>(
        this.getVotingSessionRoute(),
        this.apiService.getJsonOptions()
      )
      .toPromise();
    this.session = VotingSession.fromResponse(response);

    console.log("OPENED SESSION", response, this.session);
    this.captchaService.captchaType = this.session.requiredCaptcha;
  }

  public checkSufficientVotes(): boolean {
    if (environment.name === EnvName.Test) {
      return true; // always show results on test until TOTR voting results are more comprehensible (https://gitlab.com/fan-at/backend/api/-/issues/2592)
    }

    // temporary: use local results logic as long as its not merged in BE
    if (this.voting?.settings.show_results !== undefined) {
      return this.voting.settings.show_results;
    }

    const uniqueVoteMinimum = 10;
    const sectionVoteMinimum = 10;

    // each section must have at least 10 votes and min half of the items (but at least two) must have votes
    const sectionLimitsFulfilled = this.voting.sections.every((section) => {
      const sectionHasVotes = section.vote_count.total >= sectionVoteMinimum;
      const votedItems = section.items.filter(
        (item) => item.vote_count.total > 0
      );
      return (
        sectionHasVotes &&
        votedItems.length >= Math.max(2, section.items.length / 2)
      );
    });

    return (
      this.voting.vote_count.unique >= uniqueVoteMinimum &&
      sectionLimitsFulfilled
    );
  }

  private getVotingRoute(): string {
    return `${this.apiService.getBaseURL()}votings/${this.votingId}`;
  }

  private getVotingVoteRoute(): string {
    return `${this.apiService.getBaseURL()}votings/vote/${this.votingId}`;
  }

  private getVotingSessionRoute(): string {
    return `${this.apiService.getBaseURL()}voting-sessions/open/${
      this.votingId
    }`;
  }

  public sortItems(sortFunc) {
    this.voting.sections.forEach((section) => {
      section.items.sort(sortFunc);
    });
  }

  public userHasAlreadyVoted() {
    // checks if a user has already voted and if so, checks if he is eligible to vote again
    const lastVotedData = this.getLastVoteData();

    if (lastVotedData) {
      const lastVotedTs = +lastVotedData.time;
      return this.voting.settings.voting.allow_revote
        ? +lastVotedTs + this.voting.settings.voting.allow_revote_after_ms >
            Date.now()
        : true;
    } else {
      return false;
    }
  }

  public getLastVoteData(): VotingCookieData {
    return JSON.parse(CookieService.getCookie(this.votingStorageKey));
  }

  public get submissionMode() {
    return this.displayMode === VotingDisplayMode.SUBMISSION;
  }

  public get resultsMode() {
    return this.displayMode === VotingDisplayMode.RESULTS;
  }

  public get insufficientVotesMode() {
    return this.displayMode === VotingDisplayMode.INSUFFICIENT_VOTES;
  }

  public get voteFailed() {
    return this.localVotingStatus === LocalVotingStatus.VOTE_FAILED;
  }

  public get reviewVoteMode() {
    return this.localVotingStatus === LocalVotingStatus.REVIEW;
  }

  public get votingStorageKey() {
    return `VOTING_${this.votingId}`;
  }

  public async vote(item_ids) {
    const body = {
      sessionid: this.session._id,
      item_ids: item_ids,
      token: this.generateVotingToken(this.votingId),
      captcha_response: this.captchaResponse,
    };

    this.voting = null;
    this.session = null;

    try {
      const res: any = await this.http
        .post(
          this.getVotingVoteRoute(),
          body,
          this.apiService.getJsonHeadersInclToken()
        )
        .toPromise();
      if (res.success) {
        this.voting = new Voting(res.data);
        this.sortItems(VotingControlService.SORT_ITEMS_BY_RATING);
        this.setResultDisplaySettings();
      } else {
        return;
      }

      const cookieData: VotingCookieData = {
        time: Date.now(),
        itemids: item_ids,
      };

      CookieService.setCookieWithKey(
        this.votingStorageKey,
        JSON.stringify(cookieData),
        60 * 24
      );
      this.showResults(LocalVotingStatus.REVIEW);
      this.sessionTimeout = setTimeout(
        () => this.doAfterRevoteAllowanceElapsed(),
        this.voting.settings.voting.allow_revote_after_ms
      );
    } catch (error) {
      this.handleVoteFailure(error);
    }
  }

  private handleVoteFailure(errorResponse: any) {
    this.localVotingStatus = LocalVotingStatus.VOTE_FAILED;
    console.log(errorResponse);
  }

  private async doAfterRevoteAllowanceElapsed(): Promise<void> {
    this.captchaService.reset();
    this.onRevoteAllowanceElapsed.next();
  }

  public showResults(localStatus): VotingDisplayMode {
    this.setResultDisplaySettings();
    this.displayMode = this.checkSufficientVotes()
      ? VotingDisplayMode.RESULTS
      : VotingDisplayMode.INSUFFICIENT_VOTES;
    this.localVotingStatus = localStatus;

    return this.displayMode;
  }

  public setResultDisplaySettings() {
    if (
      [
        VotingType.PopularTeamState,
        VotingType.PopularTeamCountry.includes(this.voting?.type),
      ]
    ) {
      this.resultDisplayConfig.showOnlyTopResults = true;
      this.resultDisplayConfig.nrOfVisibleItems = 10;
    }
  }
}
